import { Page } from "./Page"
import { throttle } from "lodash"

export type TimeSeriesPageRequest = {
	pageId: number
	studyId: string
	patientId: string
	modalities: string[]
	startDate: Date | number
	endDate: Date | number
	numPoints: number
	resolution: string
}

export class TimeSeriesPageQueue<PageType extends Page<any>> {
	public loadQueue: PageType[] = []
	public unloadQueue: PageType[] = []
	public onSuccess = (page: PageType) => {}
	public onPageLoadStart = (page: PageType) => {}
	public allLoadedPages = new Map<number, PageType>()

	public activePageRequests = new Map<number, PageType>()
	private queueGeneration = 0
	private maxConcurrentRequests: number

	constructor(maxConcurrentRequests: number = 1) {
		this.maxConcurrentRequests = Math.max(1, maxConcurrentRequests)
	}

	push = (pages: PageType[]) => {
		const nonDuplicates = pages.filter(page => (
			!page.loading
			&& !this.loadQueue.find(queuedPage => page.id === queuedPage.id)
			&& !this.activePageRequests.has(page.id)
		))
		this.loadQueue.push(...nonDuplicates)
		this.drain()
	}

	unloadAllLoadedPages() {
		this.allLoadedPages.forEach(page => page.unload())
	}

	unloadOutside = throttle((neighborhood: PageType[]) => {
		const neighborhoodPages = new Set(neighborhood)

		this.loadQueue = this.loadQueue.filter(page => neighborhoodPages.has(page))
		this.unloadQueue.length = 0

		// Iterate over the keys of the Map
		for (const page of this.allLoadedPages.values()) {
			if (!neighborhoodPages.has(page)) {
				this.unloadQueue.push(page)
			}
		}

		// Delete the keys outside the loop to avoid modifying the Map while iterating
		for (const page of this.unloadQueue) {
			this.allLoadedPages.delete(page.id)
			page.unload()
		}

		for (const page of [...this.activePageRequests.values()]) {
			if (!neighborhoodPages.has(page)) {
				this.activePageRequests.delete(page.id)
				page.cancel()
			}
		}

		this.drain()
	}, 500)

	drain = () => {
		for (const page of [...this.activePageRequests.values()]) {
			if (page.needsToBeLoaded()) {
				// Then the page request is not actually loading, and needs to be re-requested, or cancelled.
				this.activePageRequests.delete(page.id)
				this.loadQueue.unshift(page)
			}
		}

		while (this.activePageRequests.size < this.maxConcurrentRequests) {
			const pageRequest = this.loadQueue.shift()

			if (!pageRequest) {
				return
			}

			this.startPageRequest(pageRequest, this.queueGeneration)
		}
	}

	private startPageRequest = (pageRequest: PageType, generation: number) => {
		this.activePageRequests.set(pageRequest.id, pageRequest)

		// Ensure partial loads trigger re-rendering.
		pageRequest.setOnPartialLoad((loadedPage: Page<any>) => {
			if (!this.isActiveRequest(loadedPage as PageType, generation)) {
				return
			}

			this.onSuccess(loadedPage as PageType)
			this.allLoadedPages.set(loadedPage.id, loadedPage as PageType)
		})

		// When the page fully loads, call onSuccess and also remove the page request from the queue.
		pageRequest.setOnFinishedLoading((loadedPage: Page<any>) => {
			if (!this.isActiveRequest(loadedPage as PageType, generation)) {
				return
			}

			// A page whose first response completes it never fires onPartialLoad, so
			// without this it would never be tracked here, and never be unloaded.
			this.allLoadedPages.set(loadedPage.id, loadedPage as PageType)
			this.onSuccess(loadedPage as PageType)
			this.activePageRequests.delete(loadedPage.id)
			this.drain()
		})

		// If the page doesn't need to be loaded for some reason, explicitly handle that early exit case.
		// This is to make sure we aren't calling the expensive "onSuccess" with the expectation that new data has arrived.
		pageRequest.setOnLoadEarlyExit(() => {
			if (!this.isActiveRequest(pageRequest, generation)) {
				return
			}

			this.activePageRequests.delete(pageRequest.id)
			this.drain()
		})

		try {
			pageRequest.load()
			this.onPageLoadStart(pageRequest)
		} catch (error) {
			console.error(error)
			pageRequest.unload()
			this.activePageRequests.delete(pageRequest.id)
			this.drain()
		}
	}

	// A callback belongs to an active request only if the queue has not been cancelled since it
	// was registered and the page is still the one in flight for its id.
	private isActiveRequest = (page: PageType, generation: number) => (
		generation === this.queueGeneration && this.activePageRequests.get(page.id) === page
	)

	cancel = () => {
		this.queueGeneration += 1
		for (const page of [...this.activePageRequests.values()]) {
			page.cancel()
		}
		this.activePageRequests.clear()
		this.clear()
	}

	clear = () => {
		this.loadQueue.length = 0
	}
}
